import type { DirItem } from "../types";

const browserFileMap = new Map<string, File>();
const browserOutputDirHandleMap = new Map<string, any>();

function getElectronAPI() {
  if (typeof window !== "undefined" && window.electronAPI) {
    return window.electronAPI;
  }
  return null;
}

function isBrowserVirtualPath(input: string) {
  return input.startsWith("browser://dir/");
}

function splitPath(input: string) {
  return input.split("/").filter(Boolean);
}

function normalizePath(input: string) {
  const slashed = input.replace(/\\/g, "/");
  if (slashed.startsWith("browser://")) {
    const rest = slashed.slice("browser://".length).replace(/\/+/g, "/").replace(/^\/+/, "");
    return `browser://${rest}`;
  }
  return slashed.replace(/\/+/g, "/");
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file, "utf-8");
  });
}

function pickDirectoryInBrowser(): Promise<string | null> {
  return new Promise(async (resolve) => {
    if (typeof window !== "undefined" && "showDirectoryPicker" in window) {
      try {
        const root = `browser://dir/${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const directoryHandle = await (window as Window & { showDirectoryPicker: () => Promise<any> }).showDirectoryPicker();

        async function walkHandle(handle: any, parent: string) {
          for await (const entry of handle.values()) {
            const relPath = parent ? `${parent}/${entry.name}` : entry.name;
            if (entry.kind === "directory") {
              await walkHandle(entry, relPath);
            } else if (entry.kind === "file") {
              const file = await entry.getFile();
              const key = `${root}/${normalizePath(relPath)}`;
              browserFileMap.set(key, file);
            }
          }
        }

        await walkHandle(directoryHandle, "");
        resolve(root);
        return;
      } catch {
        // User may cancel or browser may block the API; fallback to input[webkitdirectory].
      }
    }

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.setAttribute("webkitdirectory", "");
    input.style.display = "none";
    document.body.appendChild(input);

    input.addEventListener("change", () => {
      const files = Array.from(input.files ?? []);
      document.body.removeChild(input);
      if (files.length === 0) {
        resolve(null);
        return;
      }

      const root = `browser://dir/${Date.now()}-${Math.random().toString(16).slice(2)}`;
      for (const file of files) {
        const relative = normalizePath(file.webkitRelativePath || file.name);
        const key = `${root}/${relative}`;
        browserFileMap.set(key, file);
      }
      resolve(root);
    });

    input.click();
  });
}

function listDirInBrowser(dirPath: string): DirItem[] {
  const normalizedDir = normalizePath(dirPath).replace(/\/$/, "");
  const prefix = `${normalizedDir}/`;
  const children = new Map<string, DirItem>();

  for (const fullPath of browserFileMap.keys()) {
    if (!fullPath.startsWith(prefix)) {
      continue;
    }
    const remainder = fullPath.slice(prefix.length);
    if (!remainder) {
      continue;
    }
    const [firstSegment, ...rest] = splitPath(remainder);
    if (!firstSegment) {
      continue;
    }
    if (!children.has(firstSegment)) {
      const isDirectory = rest.length > 0;
      children.set(firstSegment, {
        name: firstSegment,
        isDirectory,
        isFile: !isDirectory
      });
    }
  }

  return Array.from(children.values());
}

async function readBrowserFile(filePath: string, mode: "utf8" | "base64"): Promise<string> {
  const file = browserFileMap.get(normalizePath(filePath));
  if (!file) {
    throw new Error(`File not found in browser memory: ${filePath}`);
  }
  if (mode === "utf8") {
    return readFileAsText(file);
  }
  const dataUrl = await readFileAsDataURL(file);
  const base64 = dataUrl.split(",")[1];
  if (!base64) {
    throw new Error(`Failed to parse base64 data: ${filePath}`);
  }
  return base64;
}

export async function pickDirectory() {
  const electron = getElectronAPI();
  if (electron) {
    return electron.pickDirectory();
  }
  return pickDirectoryInBrowser();
}

export async function pickOutputDirectory() {
  const electron = getElectronAPI();
  if (electron) {
    return electron.pickOutputDirectory();
  }
  if (typeof window !== "undefined" && "showDirectoryPicker" in window) {
    try {
      const handle = await (window as Window & { showDirectoryPicker: (options?: any) => Promise<any> }).showDirectoryPicker({
        mode: "readwrite"
      });
      const key = `browser://out/${Date.now()}-${Math.random().toString(16).slice(2)}`;
      browserOutputDirHandleMap.set(key, handle);
      return key;
    } catch {
      return null;
    }
  }
  return null;
}

export async function listDir(dirPath: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.listDir(dirPath);
  }
  if (!isBrowserVirtualPath(dirPath)) {
    throw new Error("Web mode only supports browser-selected virtual directories.");
  }
  return listDirInBrowser(dirPath);
}

export async function exists(filePath: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.exists(filePath);
  }
  return browserFileMap.has(normalizePath(filePath));
}

export async function readText(filePath: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.readFile(filePath, "utf8");
  }
  return readBrowserFile(filePath, "utf8");
}

export async function readBase64(filePath: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.readFile(filePath, "base64");
  }
  return readBrowserFile(filePath, "base64");
}

export async function saveBytes(defaultName: string, bytes: Uint8Array) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.saveBytes(defaultName, bytes);
  }
  const blob = new Blob([bytes], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = defaultName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
  return defaultName;
}

export async function writeBytesToDirectory(dirPath: string, relativeFilePath: string, bytes: Uint8Array) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.writeBytesToDir(dirPath, relativeFilePath, bytes);
  }
  if (!dirPath.startsWith("browser://out/")) {
    throw new Error("Current browser does not support direct folder writing.");
  }
  const handle = browserOutputDirHandleMap.get(dirPath);
  if (!handle) {
    throw new Error("Output folder handle expired. Please re-select output folder.");
  }
  const normalized = normalizePath(relativeFilePath);
  const parts = splitPath(normalized);
  if (parts.length === 0) {
    throw new Error("Invalid output file path");
  }
  let currentDir = handle;
  for (let i = 0; i < parts.length - 1; i += 1) {
    currentDir = await currentDir.getDirectoryHandle(parts[i], { create: true });
  }
  const fileName = parts[parts.length - 1];
  const targetHandle = await currentDir.getFileHandle(fileName, { create: true });
  const writable = await targetHandle.createWritable();
  await writable.write(bytes);
  await writable.close();
  return `${dirPath}/${normalized}`;
}

export async function joinPath(...parts: string[]) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.joinPath(...parts);
  }
  return normalizePath(parts.join("/"));
}

export async function baseName(input: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.baseName(input);
  }
  const pieces = splitPath(normalizePath(input));
  return pieces[pieces.length - 1] ?? "";
}

export async function dirName(input: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.dirName(input);
  }
  const pieces = splitPath(normalizePath(input));
  return pieces.slice(0, -1).join("/");
}

export async function extName(input: string) {
  const electron = getElectronAPI();
  if (electron) {
    return electron.extName(input);
  }
  const name = await baseName(input);
  const index = name.lastIndexOf(".");
  if (index < 0) {
    return "";
  }
  return name.slice(index);
}

export async function walkFiles(root: string): Promise<string[]> {
  const electron = getElectronAPI();
  if (electron) {
    const stack = [root];
    const files: string[] = [];

    while (stack.length) {
      const current = stack.pop()!;
      const items = await listDir(current);
      for (const item of items) {
        const nextPath = await joinPath(current, item.name);
        if (item.isDirectory) {
          stack.push(nextPath);
        } else if (item.isFile) {
          files.push(nextPath);
        }
      }
    }
    return files;
  }

  if (!isBrowserVirtualPath(root)) {
    return [];
  }
  const prefix = `${normalizePath(root).replace(/\/$/, "")}/`;
  return Array.from(browserFileMap.keys()).filter((key) => key.startsWith(prefix));
}
